import React, { useCallback, useEffect, useRef, useState } from "react";
import { CircularProgress, MenuItem } from "@mui/material";
import { debounce } from "lodash";
import CInput from "../../../components/CInput";
import { getApiData } from "../../../utils/APIHelper";
import { getCurrentLocale } from "../../../utils/Setting";

const LocationSearch = ({ value = "", onChange, disabled = false }) => {
  const [inputValue, setInputValue] = useState(value || "");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    setInputValue(value || "");
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const debouncedSearch = useCallback(
    debounce(async (q) => {
      if (!q || q.length < 3) {
        setOptions([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const locale = getCurrentLocale();
        const res = await getApiData(
          `api/project/location-list?input=${encodeURIComponent(
            q
          )}&locale=${locale}`,
          "GET",
          {}
        );
        const predictions = res?.locationList?.predictions || [];
        setOptions(
          predictions.map((p) => ({
            label: p.description,
            description: p.description,
          }))
        );
      } catch (err) {
        console.error("Location search failed:", err);
      } finally {
        setLoading(false);
      }
    }, 300),
    []
  );

  const handleSearch = (e) => {
    const v = e.target.value;
    setInputValue(v);
    if (!v.trim()) {
      setOpen(false);
      setOptions([]);
      onChange?.("");
      return;
    }
    setOpen(true);
    debouncedSearch(v.trim());
  };

  const handleSelect = (option) => {
    setInputValue(option.description);
    onChange?.(option.description);
    setOpen(false);
  };

  return (
    <div
      ref={wrapperRef}
      style={{ position: "relative", width: "100%" }}
    >
      <CInput
        label="Project Location"
        placeholder="Type to search location..."
        value={inputValue}
        onChange={handleSearch}
        onClick={() => inputValue && setOpen(true)}
        disabled={disabled}
        autoComplete="off"
      />
      {open && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 1000,
            background: "#fff",
            border: options.length > 0 || loading ? "1px solid #E5E7EB" : "none",
            borderRadius: "12px",
            boxShadow: "0 8px 24px rgba(15, 23, 42, 0.08)",
            maxHeight: "240px",
            overflowY: "auto",
            marginTop: "4px",
          }}
        >
          {loading ? (
            <MenuItem disabled>
              <CircularProgress size={16} />
              &nbsp; Searching...
            </MenuItem>
          ) : options.length === 0 ? null : (
            options.map((opt) => (
              <MenuItem
                key={opt.label}
                onClick={() => handleSelect(opt)}
                style={{
                  fontSize: 13,
                  fontFamily: "Inter Tight",
                  whiteSpace: "normal",
                }}
              >
                {opt.label}
              </MenuItem>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default LocationSearch;
